/* eslint-disable @typescript-eslint/no-unused-vars */
import { createListCollection, Combobox as ChakraCombobox, useCombobox, Portal, HStack, Span, useFilter } from '@chakra-ui/react';
import { FieldValues, Path, useController, useFormContext, PathValue } from 'react-hook-form';
import { ModalHooks } from '../modal';
import React, { useMemo, useRef, useState } from 'react';

import {
	Field,
	ComboBoxRoot,
	ComboBoxLabel,
	ComboBoxControl,
	ComboBoxInput,
	ComboBoxIndicator,
	ComboBoxIndicatorGroup,
	ComboBoxClearTrigger,
	ComboBoxTrigger,
	ComboBoxPositioner,
	ComboBoxContent,
	ComboBoxItem,
	ComboBoxItemText,
	FieldProps,
} from '../chakra-components';

import { flushSync } from 'react-dom';
import { useWatchCallback } from './useWatchCallback';
import { Res } from 'core/api';

export interface ComboBoxProps<T extends FieldValues, TPath extends Path<T>> extends Omit<ChakraCombobox.RootProps, 'collection'> {
	label?: React.ReactNode;
	placeholder: string;
	linkTo: TPath;
	options: PathValue<T, TPath>[];
	onWatch?: (value: PathValue<T, TPath>) => void;
	fieldProps?: FieldProps;
}

export type ComboBoxRef = HTMLDivElement;

export const ComboBox = <T extends FieldValues, TPath extends Path<T>>(props: ComboBoxProps<T, TPath> & { ref?: React.Ref<ComboBoxRef> }) => {
	const { ref, placeholder, fieldProps, disabled, label, linkTo, options, onWatch, ...rootProps } = props;

	const items = createListCollection<any>({
		items: options.map((option) => ({ value: option, label: String(option) })),
		itemToValue: (item) => item.value,
		itemToString: (item) => item.label,
	});

	const { control } = useFormContext<T>();
	const { fieldState, field } = useController<T>({ name: linkTo, control, disabled });

	const onItemSelected = (item: any) => field.onChange(item);

	const collection = useCreatableCombobox({
		initialItems: items.items,
		onCreateItem: (item) => {
			onItemSelected(item.value);
		},
		createOptionMode: 'append',
	});


	useWatchCallback(control, linkTo, onWatch);

	const invalid = fieldState.error?.message !== undefined;

	return (
		<Field errorText={fieldState.error?.message} invalid={invalid}  {...fieldProps} >
			<ComboBoxRoot value={collection} onChange={(e) => field.onChange((e.target as any).value)} >
				{label && <ComboBoxLabel>{label} {rootProps.required && <sup>*</sup>}</ComboBoxLabel>}
				<ComboBoxControl>
					<ComboBoxInput placeholder={placeholder} />
					<ComboBoxIndicatorGroup>
						<ComboBoxClearTrigger />
						<ComboBoxTrigger />
					</ComboBoxIndicatorGroup>
				</ComboBoxControl>

				<ComboBoxPositioner>
					<ComboBoxContent >
						{collection.collection.items.map((item) => (
							<ComboBoxItem key={item.value} item={item} onClick={() => onItemSelected(item.value)} >
								{isNewItemValue(item.value) ? (
									<ComboBoxItemText>
										{`+ ${Res.Web.COMBOBOX_CREATE_ITEM_LBL()} "${item.label}"`}
									</ComboBoxItemText>
								) : (
									<HStack justify="space-between" flex="1">
										<ComboBoxItemText flex="1">{item.label}</ComboBoxItemText>
										{item.isNew && <Span textStyle="xs">{Res.Web.COMBOBOX_NEW_ITEM_LBL()}</Span>}
									</HStack>
								)}
								<ComboBoxIndicator />
							</ComboBoxItem>
						))}
					</ComboBoxContent>
				</ComboBoxPositioner>

			</ComboBoxRoot>
		</Field>
	);
};

const isNewItemValue = (value: string) => value === NEW_ITEM_VALUE;
const NEW_ITEM_VALUE = '[[new]]';

interface Item {
	label: string
	value: string
	isNew?: boolean
}

type CreateOptionMode = 'append' | 'prepend'

interface UseCreatableComboboxProps {
	initialItems: Item[]
	onCreateItem: (item: Item) => void
	createOptionMode: CreateOptionMode
}

function useCreatableCombobox(props: UseCreatableComboboxProps) {
	const { initialItems, onCreateItem, createOptionMode } = props;

	const [items, setItems] = useState<Item[]>(initialItems);
	const itemsRef = useRef<Item[]>(initialItems);

	const { contains } = useFilter({ sensitivity: 'base' });

	const filterFn = (item: Item, query: string) =>
		!isNewItemValue(item.value) && contains(item.label, query);

	const [selectedValue, setSelectedValue] = useState<string[]>([]);

	const collection = useMemo(
		() =>
			createListCollection({
				items,
				itemToString: (item) => item.label,
				itemToValue: (item) => item.value,
			}),
		[items],
	);

	const isValidNewItem = (inputValue: string) => {
		const exactOptionMatch =
			items.filter(
				(item) => item.label.toLowerCase() === inputValue.toLowerCase(),
			).length > 0;
		return !exactOptionMatch && inputValue.trim().length > 0;
	};

	const filter = (query: string) => {
		if (isValidNewItem(query)) {
			const newItem = createNewItem(query);
			const filtered = itemsRef.current.filter((item) => filterFn(item, query));
			setItems(updateItems(filtered, newItem, createOptionMode));
			return;
		}

		if (query.trim().length === 0) {
			setItems(itemsRef.current);
		} else {
			const filtered = itemsRef.current.filter((item) => filterFn(item, query));
			setItems(filtered);
		}
	};

	const selectNewItem = (inputValue: string) => {
		const newItem = getNewItemData(inputValue);
		const filtered = itemsRef.current.filter(
			(item) => !isNewItemValue(item.value),
		);

		itemsRef.current = updateItems(filtered, newItem, createOptionMode);
		setItems(itemsRef.current);
		onCreateItem?.(newItem);
	};

	const combobox = useCombobox({
		collection,
		allowCustomValue: true,
		onInputValueChange: (details: ChakraCombobox.InputValueChangeDetails) => {
			const { inputValue, reason } = details;
			if (reason === 'input-change' || reason === 'item-select') {
				flushSync(() => filter(inputValue));
			}
		},
		onOpenChange(details) {
			const { reason, open } = details;
			if (reason === 'trigger-click') {
				setItems(itemsRef.current);
			}

			if (!open && selectedValue.length > 0) {
				const inputValue = collection.stringify(selectedValue[0]) || '';
				combobox.setInputValue(inputValue);
			}
		},
		value: selectedValue,
		onValueChange(details) {
			const { value } = details;
			const inputValue = combobox.inputValue;
			setSelectedValue(replaceNewItemValue(value, inputValue));
			if (value.includes(NEW_ITEM_VALUE)) {
				selectNewItem(inputValue);
			}
		},
	});

	return combobox;
}

const createNewItem = (value: string): Item => ({
	label: value,
	value: NEW_ITEM_VALUE,
});

const updateItems = (v: Item[], i: Item, mode: CreateOptionMode) => {
	return mode === 'prepend' ? [i, ...v] : [...v, i];
};

const getNewItemData = (inputValue: string): Item => ({
	label: inputValue,
	value: inputValue,
	isNew: true,
});

const replaceNewItemValue = (values: string[], value: string) =>
	values.map((v) => (v === NEW_ITEM_VALUE ? value : v));

